import {
  Directive, ElementRef, Input, OnChanges,
  OnDestroy, OnInit, Renderer2, SimpleChanges
} from '@angular/core';

@Directive({
  selector: '[appOptionalWorkflow]',
  standalone: true
})
export class OptionalWorkflowDirective implements OnInit, OnChanges, OnDestroy {
  @Input('appOptionalWorkflow') condition: boolean = false;
  @Input() headerText: string = 'Optional Workflow';
  @Input() headerClass: string = 'optional-workflow-header';
  @Input() initialExpanded: boolean = true;

  private headerElement: HTMLElement | null = null;
  private contentWrapper: HTMLElement | null = null;
  private arrowIcon: HTMLElement | null = null;
  private isExpanded: boolean = true;
  private listenerHeaderClick: (() => void) | null = null;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.isExpanded = this.initialExpanded;
    this.updateHeader();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['condition'] && !changes['condition']?.firstChange) 
      || (changes['headerText'] && !changes['headerText']?.firstChange)) {
      this.updateHeader();
    }
  }

  private updateHeader(): void {
    if (this.condition) {
      this.addHeader();
      this.wrapContent();
    } else {
      this.removeHeader();
      this.unwrapContent();
    }
  }

  private addHeader(): void {
    // Remove existing header if present
    this.removeHeader();

    // Create header container
    this.headerElement = this.renderer.createElement('div');
    this.renderer.addClass(this.headerElement, this.headerClass);
    this.renderer.addClass(this.headerElement, 'workflow-header');
    
    // Create arrow icon
    this.arrowIcon = this.renderer.createElement('span');
    
    // Set initial arrow state
    this.updateArrowIcon();
    
    // Create text span
    const textSpan = this.renderer.createElement('span');
    const textNode = this.renderer.createText(this.headerText);
    this.renderer.appendChild(textSpan, textNode);
    
    // Append arrow and text to header
    this.renderer.appendChild(this.headerElement, this.arrowIcon);
    this.renderer.appendChild(this.headerElement, textSpan);

    // Add click event listener
    this.listenerHeaderClick =this.renderer.listen(this.headerElement, 'click', () => {
      this.toggleContent();
    });

    // Insert header as the first child of the host element
    const hostElement = this.el.nativeElement;
    if (hostElement.firstChild) {
      this.renderer.insertBefore(hostElement, this.headerElement, hostElement.firstChild);
    } else {
      this.renderer.appendChild(hostElement, this.headerElement);
    }
  }

  private removeHeader(): void {
    if (this.headerElement) {
      this.renderer.removeChild(this.el.nativeElement, this.headerElement);
      this.headerElement = null;
      this.arrowIcon = null;
      if (this.listenerHeaderClick) {
        this.listenerHeaderClick();
        this.listenerHeaderClick = null;
      }
    }
  }

  private wrapContent(): void {
    const hostElement = this.el.nativeElement;
    
    // Create content wrapper if it doesn't exist
    if (!this.contentWrapper) {
      this.contentWrapper = this.renderer.createElement('div');
      this.renderer.addClass(this.contentWrapper, 'workflow-content');
      this.renderer.setStyle(this.contentWrapper, 'transition', 'max-height 0.3s ease, opacity 0.3s ease');
      this.renderer.setStyle(this.contentWrapper, 'overflow', 'hidden');
      
      // Move all children except header to content wrapper
      const children = Array.from(hostElement.children);
      children.forEach((child) => {
        if (child !== this.headerElement) {
          this.renderer.appendChild(this.contentWrapper, child);
        }
      });
      
      // Append content wrapper to host element
      this.renderer.appendChild(hostElement, this.contentWrapper);
    }
    
    // Set initial visibility
    this.updateContentVisibility();
  }

  private unwrapContent(): void {
    if (this.contentWrapper) {
      const hostElement = this.el.nativeElement;
      
      // Move children back to host element
      const children = Array.from(this.contentWrapper.children);
      children.forEach((child) => {
        this.renderer.appendChild(hostElement, child);
      });
      
      // Remove content wrapper
      this.renderer.removeChild(hostElement, this.contentWrapper);
      this.contentWrapper = null;
    }
  }

  private toggleContent(): void {
    this.isExpanded = !this.isExpanded;
    this.updateArrowIcon();
    this.updateContentVisibility();
  }

  private updateArrowIcon(): void {
    if (this.arrowIcon) {
    const iconClass = this.isExpanded ? 'icon-chevron-down' : 'icon-chevron-right';
    this.renderer.setAttribute(this.arrowIcon, 'class', iconClass);
    this.renderer.setProperty(this.arrowIcon, 'textContent', '');
    }
  }

  ngOnDestroy(): void {
    this.removeHeader();
    this.unwrapContent();
  }

  private updateContentVisibility(): void {
    if (this.contentWrapper) {
      if (this.isExpanded) {
        this.renderer.setStyle(this.contentWrapper, 'max-height', 'none');
        this.renderer.setStyle(this.contentWrapper, 'opacity', '1');
      } else {
        this.renderer.setStyle(this.contentWrapper, 'max-height', '0');
        this.renderer.setStyle(this.contentWrapper, 'opacity', '0');
      }
    }
  }
}